import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Mail, Calendar, Shield, Save, Lock } from 'lucide-react';
import { OWNER_EMAIL } from '../lib/permissions';
import { fmtDate, fmtDateTime } from '../utils/dateFormat';

export default function Profile({ user, logout }) {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const isOwner = user.email === OWNER_EMAIL;
  const memberSince = user.metadata?.creationTime ? fmtDate(user.metadata.creationTime) : '—';
  const lastLogin = user.metadata?.lastSignInTime ? fmtDateTime(user.metadata.lastSignInTime) : '—';

  const saveDisplayName = async (e) => {
    e.preventDefault();
    setNameMsg(null);
    setSavingName(true);
    try {
      const { updateProfile } = await import('firebase/auth');
      await updateProfile(user, { displayName: displayName.trim() || null });
      setNameMsg({ ok: true, text: 'Display name updated' });
    } catch (err) {
      setNameMsg({ ok: false, text: err.message });
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 8) {
      setPwMsg({ ok: false, text: 'New password must be at least 8 characters' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: 'New passwords do not match' });
      return;
    }
    setSavingPw(true);
    try {
      const { EmailAuthProvider, reauthenticateWithCredential, updatePassword } = await import('firebase/auth');
      const cred = EmailAuthProvider.credential(user.email, currentPw);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPw);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setPwMsg({ ok: true, text: 'Password updated' });
    } catch (err) {
      const msg = err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
        ? 'Current password is incorrect'
        : err.message;
      setPwMsg({ ok: false, text: msg });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="header-left">
          <Link to="/" className="back-btn" title="Back to portfolio"><ArrowLeft size={18} /></Link>
          <h1 className="app-title">Profile</h1>
        </div>
        <div className="header-right">
          <span className="user-email">{user.email}</span>
        </div>
      </header>

      <main className="profile-main">
        {/* Account info */}
        <section className="profile-section">
          <h2 className="profile-section-title"><User size={18} /> Account</h2>
          <div className="profile-info-grid">
            <div className="profile-info-row">
              <div className="profile-info-label"><Mail size={14} /> Email</div>
              <div className="profile-info-value">{user.email}</div>
            </div>
            <div className="profile-info-row">
              <div className="profile-info-label"><Shield size={14} /> Role</div>
              <div className="profile-info-value">
                {isOwner
                  ? <span className="profile-role-badge profile-role-owner">Owner</span>
                  : <span className="profile-role-badge">User</span>}
              </div>
            </div>
            <div className="profile-info-row">
              <div className="profile-info-label"><Calendar size={14} /> Member since</div>
              <div className="profile-info-value">{memberSince}</div>
            </div>
            <div className="profile-info-row">
              <div className="profile-info-label"><Calendar size={14} /> Last sign-in</div>
              <div className="profile-info-value">{lastLogin}</div>
            </div>
          </div>
        </section>

        {/* Display name */}
        <section className="profile-section">
          <h2 className="profile-section-title"><User size={18} /> Display Name</h2>
          <form onSubmit={saveDisplayName} className="profile-form">
            <div className="profile-field">
              <label>Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Kai"
                maxLength={60}
              />
            </div>
            <div className="profile-form-actions">
              <button type="submit" className="btn btn-primary" disabled={savingName}>
                <Save size={16} /> {savingName ? 'Saving…' : 'Save'}
              </button>
              {nameMsg && (
                <span className={`profile-msg ${nameMsg.ok ? 'ok' : 'err'}`}>{nameMsg.text}</span>
              )}
            </div>
          </form>
        </section>

        {/* Change password */}
        <section className="profile-section">
          <h2 className="profile-section-title"><Lock size={18} /> Change Password</h2>
          <form onSubmit={changePassword} className="profile-form">
            <div className="profile-field">
              <label>Current password</label>
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="profile-field">
              <label>New password (min 8 chars)</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="profile-field">
              <label>Confirm new password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="profile-form-actions">
              <button type="submit" className="btn btn-primary" disabled={savingPw}>
                <Lock size={16} /> {savingPw ? 'Updating…' : 'Update Password'}
              </button>
              {pwMsg && (
                <span className={`profile-msg ${pwMsg.ok ? 'ok' : 'err'}`}>{pwMsg.text}</span>
              )}
            </div>
          </form>
        </section>

        {/* Sign out */}
        <section className="profile-section">
          <button className="btn btn-ghost profile-signout" onClick={logout}>
            Sign out of UniHub
          </button>
        </section>
      </main>
    </div>
  );
}
